import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, Signal, signal } from '@angular/core';
import { RouteConfigLoadEnd, RouteConfigLoadStart, Router } from '@angular/router';
import { filter } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RouteLoadingService {
  private router: Router = inject(Router);
  private platformId: object = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private loadingCounter = signal(0);
  private overlayVisibleInternalSig = signal(false);
  private showOverlayTimeoutId: number | null = null;
  private hideOverlayTimeoutId: number | null = null;
  private readonly overlayShowDelayMs = 150;
  private readonly overlayHideDelayMs = 120;

  routeLoadingOverlayVisibleSig: Signal<boolean> = this.overlayVisibleInternalSig.asReadonly();

  forceHide(): void {
    this.loadingCounter.set(0);
    this.clearShowOverlayTimeout();
    this.clearHideOverlayTimeout();
    this.overlayVisibleInternalSig.set(false);
  }

  constructor() {
    if (!this.isBrowser) {
      return;
    }

    this.router.events
      .pipe(
        filter(
          (event): event is RouteConfigLoadStart | RouteConfigLoadEnd =>
            event instanceof RouteConfigLoadStart || event instanceof RouteConfigLoadEnd,
        ),
      )
      .subscribe((event) => {
        if (event instanceof RouteConfigLoadStart) {
          this.loadingCounter.update((current) => current + 1);
          if (this.loadingCounter() === 1) {
            this.scheduleShowOverlay();
          }
        } else {
          this.loadingCounter.update((current) => Math.max(0, current - 1));
          if (this.loadingCounter() === 0) {
            this.scheduleHideOverlay();
          }
        }
      });
  }

  private scheduleShowOverlay(): void {
    this.clearHideOverlayTimeout();

    if (this.overlayVisibleInternalSig() || this.showOverlayTimeoutId !== null) {
      return;
    }

    this.showOverlayTimeoutId = window.setTimeout(() => {
      this.overlayVisibleInternalSig.set(true);
      this.clearShowOverlayTimeout();
    }, this.overlayShowDelayMs);
  }

  private scheduleHideOverlay(): void {
    this.clearShowOverlayTimeout();

    if (!this.overlayVisibleInternalSig() || this.hideOverlayTimeoutId !== null) {
      return;
    }

    this.hideOverlayTimeoutId = window.setTimeout(() => {
      this.overlayVisibleInternalSig.set(false);
      this.clearHideOverlayTimeout();
    }, this.overlayHideDelayMs);
  }

  private clearShowOverlayTimeout(): void {
    if (this.showOverlayTimeoutId !== null) {
      clearTimeout(this.showOverlayTimeoutId);
      this.showOverlayTimeoutId = null;
    }
  }

  private clearHideOverlayTimeout(): void {
    if (this.hideOverlayTimeoutId !== null) {
      clearTimeout(this.hideOverlayTimeoutId);
      this.hideOverlayTimeoutId = null;
    }
  }
}
